import {
  LanguageCode,
  NamespaceId,
  type TranslationFunction,
} from 'components/translation/utils';
import {ProfileContext} from 'contexts/ProfileContext';
import {getDoc, setDoc, updateDoc} from 'firebase/firestore';
import {type TranslationNamespaceDocument} from '../../firebase/models/translationDocumentInterfaces';
import {
  getCourseTranslationDocument,
  getTranslationDocument,
  getTranslationCollection,
} from '../../firebase/models/translationDocumentReferences';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {useCollection, useDocumentData} from 'react-firebase-hooks/firestore';
import {CourseId} from 'types/common';
import {CourseContext} from 'contexts/CourseContext';
import {useTranslationUploader} from './useTranslationUploader';

interface TranslationCache {
  [languageCode: string]: {
    /** The set of namespaces that have been downloaded for the language. If a
     * namespace maps to null, it means that the namespace doesn't exist in the
     * language.
     */
    [namespace: string]: {
      [key: string]: string;
    } | null;
  };
}

interface TranslationContextData {
  /** The current language code in use. */
  siteLanguage: LanguageCode;
  /** Updates the displayed site language. Optionally additional updates the
   * user's preferred language.
   * @param newSiteLanguage - The new site language.
   * @param alsoUpdatePreferredLanguage - Whether to also update the user's
   * preferred language.
   */
  updateSiteLanguage: (
    newSiteLanguage: LanguageCode,
    alsoUpdatePreferredLanguage?: boolean,
  ) => Promise<void>;
  /** The set of supported languages for a specified course. */
  supportedLanguages: LanguageCode[];
  /** Whether the supported languages are currently loading. */
  supportedLanguagesLoading: boolean;
  /** The set of valid namespaces for the user's current course and language. */
  requestedNamespaces: NamespaceId[];
  /** Whether the requested namespaces are currently loading. */
  anyRequestedNamespacesLoading: boolean;
  /** Whether there was an error loading the requested namespaces. */
  anyRequestedNamespacesError: Error;
  /** Builds a translation function for a given namespace. */
  useTranslateFunction: (namespace: NamespaceId) => TranslationFunction;
  /** Updates the supported languages for the course in the backend. */
  updateSupportedLanguages: (
    newSupportedLanguages: LanguageCode[],
  ) => Promise<void>;
}

const TranslationContext = createContext<TranslationContextData>({
  siteLanguage: 'en',
  supportedLanguages: [],
  supportedLanguagesLoading: false,
  requestedNamespaces: [],
  anyRequestedNamespacesLoading: false,
  anyRequestedNamespacesError: null,
  // eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  useTranslateFunction: () => () => '',
  updateSupportedLanguages: async () => {},
  updateSiteLanguage: async () => {},
});

export const TranslationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    setUserData,
    userData,
    loading: userDataIsLoading,
  } = useContext(ProfileContext);

  const [siteLanguage, setSiteLanguage] = useState<LanguageCode>('en');
  const [supportedLanguages, setSupportedLanguages] = useState<LanguageCode[]>([
    'en',
  ]);
  const [supportedLanguagesLoading, setSupportedLanguagesLoading] =
    useState(true);
  const [requestedNamespaces, setRequestedNamespaces] = useState<NamespaceId[]>(
    [],
  );
  const [anyRequestedNamespacesLoading, setAnyRequestedNamespacesLoading] =
    useState(false);
  const [anyRequestedNamespacesError, setAnyRequestedNamespacesError] =
    useState<Error | null>(null);
  const [translationCache, setTranslationCache] = useState<TranslationCache>(
    {},
  );
  const {
    courseId: contextCourseId,
    courseDefaultLanguage,
    isLoading: isCourseLoading,
  } = useContext(CourseContext);
  const courseId = contextCourseId || 'public';
  const {addUnknownTranslationKeyToUploadCache} = useTranslationUploader();

  const courseTranslationDocument = getCourseTranslationDocument({courseId});

  // Load the per-course translation metadata.
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setSupportedLanguagesLoading(true);
    getDoc(courseTranslationDocument)
      .then(doc => {
        if (!doc.exists()) {
          return;
        }
        setSupportedLanguages(doc.data().supportedLanguages);
      })
      .catch(error => {
        setAnyRequestedNamespacesError(error);
      })
      .finally(() => {
        setSupportedLanguagesLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [courseId]);

  // When the user and course data are loaded, decide on the site language.
  // Prefer an explicitly set language preference, then fallback to the course
  // default language, then fallback to English.
  useEffect(() => {
    if (!userDataIsLoading && userData?.preferredLanguage) {
      updateSiteLanguage(userData.preferredLanguage, false);
    } else if (!isCourseLoading && courseDefaultLanguage) {
      updateSiteLanguage(courseDefaultLanguage, false);
    } else {
      updateSiteLanguage('en', false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [userDataIsLoading, isCourseLoading]);

  const namespacesToDownload = requestedNamespaces.filter(
    namespace => !translationCache[siteLanguage]?.[namespace],
  );
  // Only download namespaces for non-default languages.
  const areAnyNamespacesNeeded =
    siteLanguage !== 'en' && namespacesToDownload.length > 0;

  useEffect(() => {
    if (!areAnyNamespacesNeeded) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setAnyRequestedNamespacesLoading(true);

    Promise.all(
      namespacesToDownload.map(namespace => {
        return (async (): Promise<{
          [namespace: string]: TranslationNamespaceDocument | null;
        }> => {
          const doc = await getDoc(
            getTranslationDocument({
              courseId,
              language: siteLanguage,
              namespace,
            }),
          );
          if (!doc.exists()) {
            return {
              [namespace]: {},
            };
          }
          return {
            [namespace]: doc.data() ?? {},
          };
        })().catch(error => {
          console.error(
            `Failed to download translations from /translation/${courseId}/${siteLanguage}/${namespace}: ${error}`,
          );
          return {
            [namespace]: {},
          };
        });
      }),
    )
      .then(docs => {
        const newTranslationCache: TranslationCache =
          structuredClone(translationCache);
        docs.forEach(data => {
          newTranslationCache[siteLanguage] = {
            ...newTranslationCache[siteLanguage],
            ...data,
          };
        });
        setTranslationCache(newTranslationCache);
      })
      .finally(() => {
        setAnyRequestedNamespacesLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [namespacesToDownload, areAnyNamespacesNeeded, courseId, siteLanguage]);

  // This function builds the translation function for a given namespace. We
  // create this hook dynamically to allow for dynamic namespace loading, which
  // requires statefully tracking the requested namespaces for an arbitrary
  // number of namespaces. The `t` function itself cannot make stateful changes
  // as it is called within the render phase of a component, meaning a stateful
  // update would cause an infinite loop.
  const useTranslateFunction = (
    namespace: NamespaceId,
  ): TranslationFunction => {
    // If the namespace is not in the cache, we need to download it.
    useEffect(() => {
      if (translationCache[siteLanguage]?.[namespace]) {
        return;
      }
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setRequestedNamespaces(prev => {
        // Only add if not already present to prevent duplicates
        if (prev.includes(namespace)) {
          return prev;
        }
        return [...prev, namespace];
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    }, [namespace, siteLanguage, requestedNamespaces]);

    // If the namespace has been downloaded it will be in the cache (even if
    // it's null).
    const isCurrentNamespaceLoading =
      !translationCache[siteLanguage]?.[namespace];

    // This function is the "translation function" that is used to translate a
    // key to a string. Usually called `t` in the codebase, typically created as
    // `const t = useTranslateFunction('namespace')` in the codebase.
    return useCallback(
      (
        key: string,
        options?: {
          substitutions?: Record<string, string>;
        },
      ) => {
        // Shortcircuit if the key is empty (we don't want to translate empty
        // strings).
        if (!key) {
          return key;
        }

        const {substitutions} = options ?? {};
        if (isCurrentNamespaceLoading) {
          return applySubstitutions(key, substitutions);
        }
        const namespaceData = translationCache?.[siteLanguage]?.[namespace];
        if (!namespaceData) {
          return applySubstitutions(key, substitutions);
        }
        if (!namespaceData[key]) {
          addUnknownTranslationKeyToUploadCache(courseId, namespace, key);
        }

        return applySubstitutions(namespaceData[key] ?? key, substitutions);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      [namespace, siteLanguage, isCurrentNamespaceLoading],
    );
  };

  const updateSupportedLanguages = async (
    newSupportedLanguages: LanguageCode[],
  ) => {
    await updateDoc(courseTranslationDocument, {
      supportedLanguages: newSupportedLanguages,
    });
    setSupportedLanguages(newSupportedLanguages);
  };

  const updateSiteLanguage = async (
    newSiteLanguage: LanguageCode,
    alsoUpdatePreferredLanguage?: boolean,
  ) => {
    setSiteLanguage(newSiteLanguage);
    if (!alsoUpdatePreferredLanguage) {
      return;
    }
    if (userDataIsLoading || !userData) {
      return;
    }
    setUserData({preferredLanguage: newSiteLanguage}, {merge: true});
  };

  const value: TranslationContextData = {
    siteLanguage,
    supportedLanguages,
    supportedLanguagesLoading,
    requestedNamespaces,
    anyRequestedNamespacesLoading,
    anyRequestedNamespacesError,
    useTranslateFunction,
    updateSupportedLanguages,
    updateSiteLanguage,
  };
  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

interface UseSupportedNamespacesResult {
  supportedNamespaces: NamespaceId[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Lists the namespaces that are supported for a given course and language. Used
 * by admins to add/remove namespaces.
 */
export const useSupportedNamespaces = (
  courseId: CourseId,
  language: LanguageCode,
): UseSupportedNamespacesResult => {
  const [
    courseTranslationDocument,
    courseTranslationDocumentLoading,
    courseTranslationDocumentError,
  ] = useCollection(getTranslationCollection({courseId, language}));
  return {
    supportedNamespaces:
      courseTranslationDocument?.docs.map(doc => doc.id) ?? [],
    isLoading: courseTranslationDocumentLoading,
    error: courseTranslationDocumentError,
  };
};

interface UseNamespaceTranslationsResult {
  namespaceTranslations: TranslationNamespaceDocument;
  namespaceTranslationsLoading: boolean;
  namespaceTranslationsError: Error | null;
  updateNamespaceTranslations: (
    newNamespaceTranslations: TranslationNamespaceDocument,
  ) => Promise<void>;
}

export const useNamespaceTranslations = (
  courseId: CourseId,
  language: LanguageCode,
  namespace: NamespaceId,
): UseNamespaceTranslationsResult => {
  const namespaceTranslationDocumentRef = getTranslationDocument({
    courseId,
    language,
    namespace,
  });
  const [
    namespaceTranslations,
    namespaceTranslationsLoading,
    namespaceTranslationsError,
  ] = useDocumentData(namespaceTranslationDocumentRef);

  const updateNamespaceTranslations = async (
    newNamespaceTranslations: TranslationNamespaceDocument,
  ) => {
    await setDoc(namespaceTranslationDocumentRef, newNamespaceTranslations);
  };
  return {
    namespaceTranslations: namespaceTranslations ?? {},
    namespaceTranslationsLoading,
    namespaceTranslationsError,
    updateNamespaceTranslations,
  };
};

export const useTranslationContext = (): TranslationContextData => {
  return useContext(TranslationContext);
};

export const useTranslateFunction = (
  namespace: NamespaceId,
): TranslationFunction => {
  const {useTranslateFunction: useTranslateFunctionInner} =
    useTranslationContext();
  return useTranslateFunctionInner(namespace);
};

/**
 * Applies substitutions to translated text if there are any in the message.
 */
const applySubstitutions = (
  message: string,
  substitutions: Record<string, string | number | boolean> | undefined,
) => {
  if (!substitutions) {
    return message;
  }

  for (const [key, value] of Object.entries(substitutions)) {
    message = message.replaceAll(`{{${key}}}`, String(value));
  }
  return message;
};
